import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { getAbbreviatedUUID } from '@/modules/common/lib/id';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

export type FormValues = { confirmation: boolean };

export type ConfirmLearningBudgetSetupActivationModalProps = Omit<
  ModalProps,
  'title' | 'subTitle' | 'onClose'
> & {
  open: boolean;
  setupId: string;
  isLoading?: boolean;
  onClose: (data?: FormValues) => void;
};

export function ConfirmLearningBudgetSetupActivationModal({
  open,
  setupId,
  isLoading,
  onClose,
  ...props
}: ConfirmLearningBudgetSetupActivationModalProps) {
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
  } = useForm<FormValues>({
    defaultValues: { confirmation: false },
    resolver: yupResolver(
      yup.object({
        confirmation: yup
          .boolean()
          .oneOf([true], 'Please confirm activation')
          .required(),
      }),
    ),
  });

  React.useEffect(() => {
    reset();
  }, [open, reset]);

  return (
    <Modal
      open={open}
      onClose={() => onClose()}
      title={
        <span>
          Are you certain you want to activate cycle{' '}
          <span className="font-semibold text-info-dark">
            {getAbbreviatedUUID(setupId) || ''}
          </span>{' '}
          based on the current data?
        </span>
      }
      subTitle={null}
      actions={
        <>
          <Button
            variant="outlined"
            type="button"
            onClick={() => onClose()}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="confirm-cycle-activation-form"
            disabled={!isValid}
            loading={isSubmitting || isLoading}
          >
            Confirm
          </Button>
        </>
      }
      classes={{ paper: 'max-w-2xl' }}
      {...props}
    >
      <form
        id="confirm-cycle-activation-form"
        onSubmit={handleSubmit(onClose)}
        className="py-2"
      >
        <Stack gap={2}>
          <Typography variant="body1">
            Activating this cycle will lock in the current configuration. Please
            double-check all fields before continuing.
          </Typography>
          <FormControlLabel
            control={
              <Controller
                control={control}
                name="confirmation"
                render={({ field }) => (
                  <Checkbox {...field} checked={field.value} />
                )}
              />
            }
            label={
              <span className="text-sm">
                I confirm activation of this cycle.
              </span>
            }
          />
        </Stack>
      </form>
    </Modal>
  );
}
